class SearchService {
  constructor() {
    this.baseURL = '/api/search';
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
  }

  // Text Search for locations, neighborhoods, cities
  async searchLocations(query, filters = {}) {
    const cacheKey = `locations:${query}:${JSON.stringify(filters)}`;
    const cached = this.getFromCache(cacheKey);
    if (cached) return cached;

    try {
      const params = new URLSearchParams({
        q: query,
        ...filters
      });

      const response = await fetch(`${this.baseURL}/locations?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`Search failed: ${response.statusText}`);
      }

      const data = await response.json();
      this.setCache(cacheKey, data);
      return data;
    } catch (error) {
      console.error('Location search failed:', error);
      throw error;
    }
  }

  // Geospatial search - find places near coordinates with advanced filters
  async searchNearby(coordinates, options = {}) {
    const { lat, lng } = coordinates;
    const {
      radius = 1000, // meters
      type = null,
      category = null,
      minRating = null,
      limit = 20,
      
      // Advanced filter support
      safetyZones = [],
      locationTypes = [],
      categories = [],
      minSafetyRating = null,
      hasPhotos = null,
      verifiedOnly = false,
      reviewTimeRange = 'all'
    } = options;

    const cacheKey = `nearby:${lat}:${lng}:${JSON.stringify(options)}`;
    const cached = this.getFromCache(cacheKey);
    if (cached) return cached;

    try {
      const params = new URLSearchParams({
        lat: lat.toString(),
        lng: lng.toString(),
        radius: radius.toString(),
        limit: limit.toString()
      });

      // Legacy filters
      if (type) params.append('type', type);
      if (category) params.append('category', category);
      if (minRating) params.append('minRating', minRating.toString());

      // Advanced filters
      if (safetyZones.length > 0) {
        safetyZones.forEach(zone => params.append('safetyZones', zone));
      }
      if (locationTypes.length > 0) {
        locationTypes.forEach(locType => params.append('locationTypes', locType));
      }
      if (categories.length > 0) {
        categories.forEach(cat => params.append('categories', cat));
      }
      if (minSafetyRating) params.append('minSafetyRating', minSafetyRating.toString());
      if (hasPhotos !== null) params.append('hasPhotos', hasPhotos.toString());
      if (verifiedOnly) params.append('verifiedOnly', 'true');
      if (reviewTimeRange !== 'all') params.append('reviewTimeRange', reviewTimeRange);

      const response = await fetch(`${this.baseURL}/nearby?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`Nearby search failed: ${response.statusText}`);
      }

      const data = await response.json();
      this.setCache(cacheKey, data);
      return data;
    } catch (error) {
      console.error('Nearby search failed:', error);
      throw error;
    }
  }

  // Autocomplete suggestions
  async getAutocompleteSuggestions(query, options = {}) {
    const { type = 'all', limit = 10 } = options;
    
    if (query.length < 2) return { suggestions: [] };

    const cacheKey = `autocomplete:${query}:${type}`;
    const cached = this.getFromCache(cacheKey);
    if (cached) return cached;

    try {
      const params = new URLSearchParams({
        q: query,
        type,
        limit: limit.toString()
      });

      const response = await fetch(`${this.baseURL}/autocomplete?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`Autocomplete failed: ${response.statusText}`);
      }

      const data = await response.json();
      this.setCache(cacheKey, data);
      return data;
    } catch (error) {
      console.error('Autocomplete failed:', error);
      return { suggestions: [] };
    }
  }

  // Full-text search in reviews
  async searchReviews(query, filters = {}) {
    const cacheKey = `reviews:${query}:${JSON.stringify(filters)}`;
    const cached = this.getFromCache(cacheKey);
    if (cached) return cached;

    try {
      const params = new URLSearchParams({
        q: query,
        ...filters
      });

      const response = await fetch(`${this.baseURL}/reviews?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`Review search failed: ${response.statusText}`);
      }

      const data = await response.json();
      this.setCache(cacheKey, data);
      return data;
    } catch (error) {
      console.error('Review search failed:', error);
      throw error;
    }
  }

  // Advanced search with comprehensive filter support
  async advancedSearch(criteria) {
    const {
      query = '',
      location = null,
      radius = 1000,
      
      // Safety filters
      safetyZones = [],
      minSafetyRating = null,
      
      // Time-based filters
      reviewTimeRange = 'all',
      customDateRange = { start: null, end: null },
      lastUpdated = null,
      
      // Location type filters
      locationTypes = [],
      categories = [],
      neighborhoods = [],
      
      // Rating filters
      minRating = null,
      minReviewCount = null,
      hasPhotos = null,
      hasReviews = null,
      verifiedOnly = false,
      
      // Distance filters
      distanceRadius = null,
      centerPoint = null,
      
      // Legacy filters (for backward compatibility)
      safetyRating = null,
      locationType = null,
      category = null,
      timeOfDay = null,
      
      sortBy = 'relevance',
      limit = 20,
      offset = 0
    } = criteria;

    const cacheKey = `advanced:${JSON.stringify(criteria)}`;
    const cached = this.getFromCache(cacheKey);
    if (cached) return cached;

    try {
      const params = new URLSearchParams({
        q: query,
        sortBy,
        limit: limit.toString(),
        offset: offset.toString()
      });

      // Location and distance parameters
      const searchCenter = centerPoint || location;
      const searchRadius = distanceRadius || radius;
      
      if (searchCenter) {
        params.append('lat', searchCenter.lat.toString());
        params.append('lng', searchCenter.lng.toString());
        params.append('radius', searchRadius.toString());
      }

      // Safety zone filters
      if (safetyZones.length > 0) {
        safetyZones.forEach(zone => params.append('safetyZones', zone));
      }
      if (minSafetyRating) params.append('minSafetyRating', minSafetyRating.toString());

      // Time-based filters
      if (reviewTimeRange !== 'all') params.append('reviewTimeRange', reviewTimeRange);
      if (customDateRange.start) params.append('dateRangeStart', customDateRange.start);
      if (customDateRange.end) params.append('dateRangeEnd', customDateRange.end);
      if (lastUpdated) params.append('lastUpdated', lastUpdated);

      // Location type filters
      if (locationTypes.length > 0) {
        locationTypes.forEach(type => params.append('locationTypes', type));
      }
      if (categories.length > 0) {
        categories.forEach(cat => params.append('categories', cat));
      }
      if (neighborhoods.length > 0) {
        neighborhoods.forEach(hood => params.append('neighborhoods', hood));
      }

      // Rating filters
      if (minRating) params.append('minRating', minRating.toString());
      if (minReviewCount) params.append('minReviewCount', minReviewCount.toString());
      if (hasPhotos !== null) params.append('hasPhotos', hasPhotos.toString());
      if (hasReviews !== null) params.append('hasReviews', hasReviews.toString());
      if (verifiedOnly) params.append('verifiedOnly', 'true');

      // Legacy filters (for backward compatibility)
      if (safetyRating) params.append('safetyRating', safetyRating.toString());
      if (locationType) params.append('type', locationType);
      if (category) params.append('category', category);
      if (timeOfDay) params.append('timeOfDay', timeOfDay);

      const response = await fetch(`${this.baseURL}/advanced?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`Advanced search failed: ${response.statusText}`);
      }

      const data = await response.json();
      this.setCache(cacheKey, data);
      return data;
    } catch (error) {
      console.error('Advanced search failed:', error);
      throw error;
    }
  }

  // Search history management
  async getSearchHistory() {
    try {
      const response = await fetch(`${this.baseURL}/history`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`
        }
      });

      if (!response.ok) {
        throw new Error(`History fetch failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Search history fetch failed:', error);
      return { history: [] };
    }
  }

  async saveSearchToHistory(searchTerm, searchType = 'location', metadata = {}) {
    try {
      const response = await fetch(`${this.baseURL}/history`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`
        },
        body: JSON.stringify({
          searchTerm,
          searchType,
          metadata,
          timestamp: new Date().toISOString()
        })
      });

      if (!response.ok) {
        throw new Error(`History save failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Search history save failed:', error);
    }
  }

  async clearSearchHistory() {
    try {
      const response = await fetch(`${this.baseURL}/history`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`
        }
      });

      return response.ok;
    } catch (error) {
      console.error('Search history clear failed:', error);
      return false;
    }
  }

  // Popular searches and trending
  async getPopularSearches(timeframe = 'week') {
    const cacheKey = `popular:${timeframe}`;
    const cached = this.getFromCache(cacheKey);
    if (cached) return cached;

    try {
      const params = new URLSearchParams({ timeframe });
      const response = await fetch(`${this.baseURL}/popular?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`Popular searches failed: ${response.statusText}`);
      }

      const data = await response.json();
      this.setCache(cacheKey, data, 30 * 60 * 1000); // 30 minutes cache
      return data;
    } catch (error) {
      console.error('Popular searches failed:', error);
      return { popular: [] };
    }
  }

  // Location type and category suggestions
  getLocationTypes() {
    return [
      { value: 'restaurant', label: 'Restaurant', icon: '🍽️' },
      { value: 'hotel', label: 'Hotel', icon: '🏨' },
      { value: 'attraction', label: 'Tourist Attraction', icon: '🗼' },
      { value: 'transport', label: 'Transportation', icon: '🚇' },
      { value: 'shopping', label: 'Shopping', icon: '🛍️' },
      { value: 'nightlife', label: 'Nightlife', icon: '🌙' },
      { value: 'park', label: 'Park', icon: '🌳' },
      { value: 'beach', label: 'Beach', icon: '🏖️' },
      { value: 'museum', label: 'Museum', icon: '🏛️' },
      { value: 'hospital', label: 'Hospital', icon: '🏥' },
      { value: 'police', label: 'Police Station', icon: '🚔' },
      { value: 'embassy', label: 'Embassy', icon: '🏛️' },
      { value: 'bank', label: 'Bank', icon: '🏦' },
      { value: 'pharmacy', label: 'Pharmacy', icon: '💊' },
      { value: 'gas-station', label: 'Gas Station', icon: '⛽' },
      { value: 'airport', label: 'Airport', icon: '✈️' },
      { value: 'bus-station', label: 'Bus Station', icon: '🚌' },
      { value: 'train-station', label: 'Train Station', icon: '🚂' },
      { value: 'subway', label: 'Subway Station', icon: '🚇' }
    ];
  }

  getLocationCategories() {
    return [
      { value: 'dining', label: 'Dining', icon: '🍽️' },
      { value: 'accommodation', label: 'Accommodation', icon: '🏨' },
      { value: 'entertainment', label: 'Entertainment', icon: '🎭' },
      { value: 'transportation', label: 'Transportation', icon: '🚇' },
      { value: 'shopping', label: 'Shopping', icon: '🛍️' },
      { value: 'health', label: 'Health & Medical', icon: '🏥' },
      { value: 'education', label: 'Education', icon: '🎓' },
      { value: 'government', label: 'Government', icon: '🏛️' },
      { value: 'finance', label: 'Finance', icon: '🏦' },
      { value: 'recreation', label: 'Recreation', icon: '🎯' },
      { value: 'culture', label: 'Culture', icon: '🎨' },
      { value: 'emergency', label: 'Emergency Services', icon: '🚨' }
    ];
  }

  // Cache management
  getFromCache(key) {
    const cached = this.cache.get(key);
    if (cached && (Date.now() - cached.timestamp) < this.cacheTimeout) {
      return cached.data;
    }
    this.cache.delete(key);
    return null;
  }

  setCache(key, data, timeout = this.cacheTimeout) {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });

    // Clean old cache entries periodically
    if (this.cache.size > 100) {
      const keys = Array.from(this.cache.keys()).slice(0, 20);
      keys.forEach(k => this.cache.delete(k));
    }
  }

  clearCache() {
    this.cache.clear();
  }

  // Utility methods
  getAuthToken() {
    return localStorage.getItem('authToken') || '';
  }

  // Geolocation utilities
  async getCurrentLocation() {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
        },
        (error) => {
          reject(new Error(`Location error: ${error.message}`));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        }
      );
    });
  }

  // Distance calculation
  calculateDistance(point1, point2) {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = point1.lat * Math.PI / 180;
    const φ2 = point2.lat * Math.PI / 180;
    const Δφ = (point2.lat - point1.lat) * Math.PI / 180;
    const Δλ = (point2.lng - point1.lng) * Math.PI / 180;

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return R * c; // Distance in meters
  }

  formatDistance(meters) {
    if (meters < 1000) {
      return `${Math.round(meters)}m`;
    } else if (meters < 10000) {
      return `${(meters / 1000).toFixed(1)}km`;
    } else {
      return `${Math.round(meters / 1000)}km`;
    }
  }

  // Search result processing
  processSearchResults(results, userLocation = null) {
    return results.map(result => {
      const processed = {
        ...result,
        distance: null,
        formattedDistance: null
      };

      // Calculate distance if user location is available
      if (userLocation && result.coordinates) {
        const distance = this.calculateDistance(
          userLocation,
          {
            lat: result.coordinates.coordinates[1],
            lng: result.coordinates.coordinates[0]
          }
        );
        processed.distance = distance;
        processed.formattedDistance = this.formatDistance(distance);
      }

      // Format safety rating
      if (result.safetyInfo) {
        processed.safetyLevel = this.getSafetyLevel(result.safetyInfo.averageRating);
      }

      return processed;
    });
  }

  getSafetyLevel(rating) {
    if (rating >= 4.5) return { level: 'very-safe', color: '#4CAF50', icon: '🛡️' };
    if (rating >= 3.5) return { level: 'safe', color: '#8BC34A', icon: '✅' };
    if (rating >= 2.5) return { level: 'moderate', color: '#FF9800', icon: '⚠️' };
    if (rating >= 1.5) return { level: 'caution', color: '#FF5722', icon: '⚡' };
    return { level: 'unsafe', color: '#F44336', icon: '🚨' };
  }
}

// Singleton instance
const searchService = new SearchService();
export default searchService;